import React from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import { merge } from 'lodash';
import { vars } from '$util/theme';
import type { SchemaType } from '$util/schema';
import type { ContentComponent } from '$cms/ContentComponent';
import { CmsImageSchema, CmsShortTextSchema } from '$cms/types';
import { Link } from '$quantum/atoms/Link';
import { showLiveChat } from '$components/LiveChat';
import { ResponsiveImage } from '$quantum/atoms/ResponsiveImage';
import { makeContentComponentDefaultConfig } from '$util/makeContentComponentDefaultConfig';
import { Button, DesignType } from '@/components/atoms/Button';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import { Col, Row } from '@/components/atoms/Grid';
import { ContactItem } from './ContactItem';
import styles from './index.module.scss';

const defaultConfiguration = makeContentComponentDefaultConfig({
  backgroundColor: vars.quantumverse.reskinColors.purple300,
});

export const HelpSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    subHeading: CmsShortTextSchema,
    image: CmsImageSchema,
    contactItemHeading: z.object({
      1: CmsShortTextSchema,
      2: CmsShortTextSchema,
      3: CmsShortTextSchema,
    }),
    contactItemSubheading: z.object({
      1: CmsShortTextSchema,
      2: CmsShortTextSchema,
      3: CmsShortTextSchema,
    }),
    contactItemIcon: z.object({
      1: CmsImageSchema,
      2: CmsImageSchema,
      3: CmsImageSchema,
    }),
    contactItemCTATitle: z.object({
      1: CmsShortTextSchema,
    }),
  }),
  configuration: z
    .object({
      backgroundColor: z.string().optional(),
      anchorId: z.string().optional(),
      lazyLoadImages: z.boolean().optional(),
      preloadImages: z.boolean().optional(),
    })
    .default(defaultConfiguration),
};

export type HelpTranslatedEntities = SchemaType<(typeof HelpSchema)['translatedEntities']>;
export type HelpConfiguration = SchemaType<(typeof HelpSchema)['configuration']>;

/**
 * This is a crucial component for generating sales leads.<br />
 * https://www.notion.so/fixter/Help-01cca849c89d4a0fb0e5fb61aa42816f
 */
export const Help: ContentComponent<HelpTranslatedEntities, HelpConfiguration> = function Help({
  translatedEntities,
  configuration,
  className,
}): JSX.Element {
  const {
    heading,
    subHeading,
    image,
    contactItemCTATitle,
    contactItemHeading,
    contactItemIcon,
    contactItemSubheading,
  } = translatedEntities;
  const { backgroundColor, anchorId, lazyLoadImages, preloadImages } = merge(
    defaultConfiguration,
    configuration
  );

  return (
    <ContentComponentSection
      id={anchorId}
      className={classNames(styles.Help, className)}
      style={{
        // @ts-ignore Pass prop to styles
        '--bg-color': backgroundColor,
      }}
    >
      <Row>
        <Col large={7} className={styles.info}>
          <HeadingLevels className={styles.Heading} semanticLevel={2} styleLevel={2}>
            {heading.value}
          </HeadingLevels>
          <HeadingLevels className={styles.Subheading} semanticLevel={3} styleLevel={3}>
            {subHeading?.value}
          </HeadingLevels>
          <div className={styles.flex}>
            <ContactItem
              icon={contactItemIcon[1]}
              heading={contactItemHeading[1]}
              subHeading={
                <Link type="big" href={`tel:${contactItemSubheading[1]?.value?.trim()}`}>
                  {contactItemSubheading[1].value}
                </Link>
              }
              extraElement={
                <Button
                  href={`tel:${contactItemSubheading[1]?.value?.trim()}`}
                  designType={DesignType.CTA_DARK}
                  className={styles.outerButton}
                  size="S"
                  noIcon
                >
                  {contactItemCTATitle[1].value}
                </Button>
              }
            />
            <ContactItem
              icon={contactItemIcon[2]}
              heading={contactItemHeading[2]}
              subHeading={
                <Button
                  designType={DesignType.CTA_DARK}
                  noIcon
                  onClick={showLiveChat}
                  size="S"
                  className={styles.innerButton}
                >
                  {contactItemSubheading[2].value}
                </Button>
              }
              extraElement={
                <Button
                  designType={DesignType.CTA_DARK}
                  noIcon
                  onClick={showLiveChat}
                  size="S"
                  className={styles.outerButton}
                >
                  {contactItemSubheading[2].value}
                </Button>
              }
            />
            <ContactItem
              icon={contactItemIcon[3]}
              heading={contactItemHeading[3]}
              subHeading={
                <Link type="big" href={`mailto:${contactItemSubheading[3].value}`}>
                  {contactItemSubheading[3].value}
                </Link>
              }
            />
          </div>
        </Col>
        <Col large={5} className={styles.imageWrapper}>
          <ResponsiveImage
            className="help-image"
            layout="intrinsic"
            fill="natural"
            objectFit="cover"
            src={image.url}
            alt={image.alt}
            width={image.width}
            height={image.height}
            key={image.key}
            title={image.title}
            lazy={lazyLoadImages}
            preload={preloadImages}
          />
        </Col>
      </Row>
    </ContentComponentSection>
  );
};

Help.displayName = 'Help';
Help.schema = HelpSchema;
